/**
 * Background sync for offline POS data (desktop app only).
 * Fetches products, customers, salesmen from API and stores in local SQLite via electronAPI.
 * Syncs pending offline bills to backend when online (Phase 4 reconciliation).
 * Only runs when window.electronAPI exists - web and Android are unaffected.
 */

import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { isAuthenticated, getCurrentUser } from '../../services/authService';
import productService from '../../services/productService';
import { customerService } from '../../services/customerService';
import salesmanService from '../../services/salesmanService';
import salesService from '../../services/salesService';
import { OfflineContext } from '../../context/OfflineContext';
import { getDesktopOfflineCompanyId } from '../../utils/desktopOffline';

const SYNC_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const INITIAL_SYNC_DELAY_MS = 5000; // 5 seconds after mount (let app settle)
/** How often reconciliation runs on a timer alone (browser may stay "online" when only the backend is down). */
const RECONCILIATION_INTERVAL_MS = 15 * 1000;
const RECONCILIATION_INITIAL_DELAY_MS = 1000; // 1 second after mount
const PENDING_POLL_INTERVAL_MS = 5000; // 5 seconds - refresh pending count + (throttled) auto-reconcile attempt
/** Min gap between automatic reconcile attempts while bills are pending (avoids hammering the API). */
const AUTO_RECONCILE_THROTTLE_MS = 8000;

/**
 * Normalize location product to common product format for offline DB.
 */
function normalizeLocationProduct(lp) {
  return {
    id: lp.productId,
    name: lp.productName,
    sku: lp.sku || lp.productCode,
    barcode: lp.barcode || lp.productBarcode || lp.productCode,
    sellingPrice: lp.sellingPrice ?? lp.purchasePrice ?? 0,
    wholesalePrice: lp.wholesalePrice ?? lp.wholesale_price ?? null,
    quantity: lp.availableForSale ?? lp.quantity ?? 0,
    unit: lp.unit || lp.productUnit || null,
    tax: lp.tax || null,
    taxId: lp.taxId ?? lp.tax?.id ?? null,
    taxRate: lp.tax?.rate ?? lp.taxRate ?? 0,
    companyId: lp.companyId ?? lp.company_id ?? null,
    locationId: lp.locationId ?? lp.location_id ?? null,
    status: lp.status || 'ACTIVE',
    parentProductId: lp.parentProductId ?? lp.parent_product_id ?? null
  };
}

/**
 * Normalize warehouse product (already in correct format, ensure fields exist).
 */
function normalizeProduct(p) {
  return {
    id: p.id,
    name: p.name,
    sku: p.sku,
    barcode: p.barcode,
    sellingPrice: p.sellingPrice ?? p.selling_price ?? 0,
    wholesalePrice: p.wholesalePrice ?? p.wholesale_price ?? null,
    quantity: p.quantity ?? 0,
    unit: p.unit || null,
    tax: p.tax || null,
    taxId: p.taxId ?? p.tax?.id ?? null,
    taxRate: p.tax?.rate ?? p.taxRate ?? 0,
    companyId: p.companyId ?? p.company_id ?? null,
    locationId: p.locationId ?? p.location_id ?? null,
    status: p.status || 'ACTIVE',
    parentProductId: p.parentProductId ?? p.parent_product_id ?? null
  };
}

/**
 * Get walk-in customer ID from offline customers (for bills with no customer selected).
 */
async function getWalkInCustomerId(electronAPI) {
  if (!electronAPI?.getOfflineCustomers) return null;
  const customers = await electronAPI.getOfflineCustomers(getDesktopOfflineCompanyId()) || [];
  const walkIn = customers.find(
    (c) => c && (String(c.name || '').toLowerCase().includes('walk-in') || String(c.name || '').toLowerCase().includes('walkin'))
  );
  return walkIn?.id ?? null;
}

/**
 * Run reconciliation: push pending offline bills to backend when online.
 * Called periodically and on manual "Sync now" button.
 * @returns {Promise<number>} Number of bills successfully synced
 */
async function runReconciliation(electronAPI, onMarkSynced, onMarkFailed) {
  if (!electronAPI?.getOfflinePendingOrders || !electronAPI?.markOrderSynced || !electronAPI?.markOrderSyncFailed) return 0;
  const pending = await electronAPI.getOfflinePendingOrders();
  if (!pending || pending.length === 0) return 0;
  // Current build's server URL — used to filter out bills saved for a different backend.
  const currentServerUrl = process.env.REACT_APP_API_URL || '';
  let walkInId = null;
  let syncedCount = 0;
  let authFailed = false;
  for (const row of pending) {
    if (authFailed) break; // No point retrying remaining bills with an expired token
    const { local_id, order_data } = row;
    if (!order_data || !order_data.items?.length) {
      await onMarkFailed(local_id, 'Invalid order data');
      continue;
    }
    // Skip bills that were saved while connected to a different backend (e.g. prod bill in
    // local app). Only skip when BOTH sides have a known URL so legacy bills (no _serverUrl)
    // are still retried normally.
    const billServerUrl = order_data._serverUrl || '';
    if (billServerUrl && currentServerUrl && billServerUrl !== currentServerUrl) {
      await onMarkFailed(local_id, `Bill belongs to a different server (${billServerUrl}). Open the correct app to sync it.`);
      continue;
    }
    // Strip all internal metadata fields before sending to the backend API.
    const { _serverUrl, _customerName, _itemNames, ...payload } = { ...order_data };
    if (payload.customerId == null) {
      walkInId = walkInId ?? await getWalkInCustomerId(electronAPI);
      if (walkInId != null) payload.customerId = walkInId;
      else {
        await onMarkFailed(local_id, 'Customer required. Add Walk-in Customer and sync data before going offline.');
        continue;
      }
    }
    try {
      const result = await salesService.createSalesOrder(payload);
      const backendId = result?.id ?? result?.orderId ?? null;
      if (backendId != null) {
        await onMarkSynced(local_id, backendId);
        syncedCount++;
      } else {
        await onMarkFailed(local_id, 'Backend did not return order ID');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        // Auth token expired — remaining bills will all fail too. Stop and let the caller
        // show a re-login prompt rather than marking every bill as individually failed.
        await onMarkFailed(local_id, 'Session expired. Please log in again to sync offline bills.');
        authFailed = true;
        continue;
      }
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      await onMarkFailed(local_id, msg);
    }
  }
  return syncedCount;
}

/**
 * OfflineSyncProvider - runs background sync when in desktop app and user is logged in.
 * Also syncs pending offline bills to backend when online (Phase 4 reconciliation).
 * Provides OfflineContext for pending count and manual sync.
 */
export default function OfflineSyncProvider({ children }) {
  const intervalRef = useRef(null);
  const reconciliationRef = useRef(null);
  const pendingPollRef = useRef(null);
  /** True while reconciliation is running (interval + stale closure safe). */
  const reconcileInFlightRef = useRef(false);
  /** Last time we kicked an automatic reconcile from the pending-count poll (throttle). */
  const lastAutoReconcileAtRef = useRef(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const refreshPendingCount = useCallback(async () => {
    if (!window.electronAPI?.getOfflinePendingCount) return 0;
    try {
      const count = await window.electronAPI.getOfflinePendingCount();
      const n = count ?? 0;
      setPendingCount(n);
      if (n > 0) console.log('[OfflineSync] Pending bills:', n);
      return n;
    } catch (e) {
      console.warn('[OfflineSync] getOfflinePendingCount failed:', e);
      setPendingCount(0);
      return 0;
    }
  }, []);

  const runReconciliationWithContext = useCallback(async (showSnackbar = false) => {
    if (!window.electronAPI || !isAuthenticated()) return;
    if (reconcileInFlightRef.current) return;
    reconcileInFlightRef.current = true;
    setIsSyncing(true);
    let lastError = null;
    try {
      const pending = await window.electronAPI.getOfflinePendingOrders();
      const beforeCount = pending?.length ?? 0;
      const syncedCount = await runReconciliation(
        window.electronAPI,
        (localId, backendId) => window.electronAPI.markOrderSynced(localId, backendId),
        (localId, err) => { lastError = err; return window.electronAPI.markOrderSyncFailed(localId, err); }
      );
      await refreshPendingCount();
      // Always show notification when bills sync (manual or automatic)
      if (syncedCount > 0) {
        enqueueSnackbar(`${syncedCount} offline bill${syncedCount > 1 ? 's' : ''} synced successfully`, { variant: 'success' });
      }
      // Show re-login message when session expired during sync
      if (lastError && lastError.includes('Session expired')) {
        enqueueSnackbar('Session expired. Please log in again to sync offline bills.', { variant: 'warning', persist: false });
      } else if (lastError && lastError.includes('belongs to a different server')) {
        enqueueSnackbar('Some bills were saved on a different server and cannot sync here. Open the correct app to sync them.', { variant: 'info' });
      } else if (showSnackbar && beforeCount > 0 && syncedCount === 0 && lastError) {
        enqueueSnackbar(`Sync failed: ${lastError}`, { variant: 'error' });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Sync failed';
      if (showSnackbar) enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      reconcileInFlightRef.current = false;
      setIsSyncing(false);
    }
  }, [enqueueSnackbar, refreshPendingCount]);

  /**
   * Poll pending count and, while bills are queued, periodically try reconciliation.
   * This covers backend recovery when navigator stayed "online" (no browser `online` event).
   */
  const pendingPollTick = useCallback(async () => {
    const runReconcile = runReconciliationWithContext;
    if (!window.electronAPI?.getOfflinePendingCount) return;
    const n = await refreshPendingCount();
    if (!n || n <= 0) return;
    if (reconcileInFlightRef.current) return;
    const now = Date.now();
    if (now - lastAutoReconcileAtRef.current < AUTO_RECONCILE_THROTTLE_MS) return;
    lastAutoReconcileAtRef.current = now;
    void runReconcile(false);
  }, [refreshPendingCount, runReconciliationWithContext]);

  /** Call after POS saves an offline bill: reset throttle + try once so reconnect syncs ASAP. */
  const kickOfflineReconcile = useCallback(() => {
    lastAutoReconcileAtRef.current = 0;
    void pendingPollTick();
  }, [pendingPollTick]);

  const runSync = useCallback(async () => {
    if (!window.electronAPI || !isAuthenticated()) return;

    try {
      const user = getCurrentUser();
      const hasLocation = user?.location?.id != null;

      // 1. Fetch products (location-specific or all)
      let products = [];
      try {
        if (hasLocation) {
          const locationProducts = await productService.getLocationProducts();
          products = (locationProducts || []).map(normalizeLocationProduct);
        } else {
          const res = await productService.getAllProducts(0, 1000, 'name', 'asc');
          const list = Array.isArray(res) ? res : (res?.content || []);
          products = list.map(normalizeProduct);
        }
        if (products.length > 0 && window.electronAPI.upsertOfflineProducts) {
          await window.electronAPI.upsertOfflineProducts(products);
          console.log('[OfflineSync] Products synced:', products.length);
        }
      } catch (e) {
        console.warn('[OfflineSync] Products fetch failed:', e?.message || e);
      }

      // 2. Fetch customers
      try {
        const res = await customerService.getAllCustomers({ page: 0, size: 500, sort: 'name', direction: 'ASC' });
        const customers = Array.isArray(res) ? res : (res?.content || []);
        if (customers.length > 0 && window.electronAPI.upsertOfflineCustomers) {
          await window.electronAPI.upsertOfflineCustomers(customers);
          console.log('[OfflineSync] Customers synced:', customers.length);
        }
      } catch (e) {
        console.warn('[OfflineSync] Customers fetch failed:', e?.message || e);
      }

      // 3. Fetch salesmen
      try {
        const locationId = user?.location?.id ?? null;
        const list = await salesmanService.list(locationId != null ? { locationId } : {});
        const salesmen = Array.isArray(list) ? list : [];
        if (salesmen.length > 0 && window.electronAPI.upsertOfflineSalesmen) {
          await window.electronAPI.upsertOfflineSalesmen(salesmen);
          console.log('[OfflineSync] Salesmen synced:', salesmen.length);
        }
      } catch (e) {
        console.warn('[OfflineSync] Salesmen fetch failed:', e?.message || e);
      }
    } catch (err) {
      console.warn('[OfflineSync] Sync error:', err?.message || err);
    }
  }, []);

  useEffect(() => {
    if (!window.electronAPI) {
      console.log('[OfflineSync] Skipped: not in Electron');
      return;
    }
    if (!isAuthenticated()) {
      console.log('[OfflineSync] Skipped: not authenticated');
      return;
    }
    console.log('[OfflineSync] Started: polling + throttled auto-reconcile when pending, timer every', RECONCILIATION_INTERVAL_MS / 1000, 's');

    // Initial sync after delay
    const initialTimer = setTimeout(runSync, INITIAL_SYNC_DELAY_MS);

    // Periodic sync (products, customers, salesmen)
    intervalRef.current = setInterval(runSync, SYNC_INTERVAL_MS);

    // Phase 4: Reconciliation timer (backup — primary auto-reconcile is tied to pending poll)
    const initialReconcile = setTimeout(() => runReconciliationWithContext(true), RECONCILIATION_INITIAL_DELAY_MS);
    reconciliationRef.current = setInterval(() => runReconciliationWithContext(false), RECONCILIATION_INTERVAL_MS);

    pendingPollTick();
    pendingPollRef.current = setInterval(pendingPollTick, PENDING_POLL_INTERVAL_MS);

    // Trigger sync immediately when network/backend connectivity is restored
    const handleOnline = () => {
      console.log('[OfflineSync] Network restored — triggering immediate sync');
      lastAutoReconcileAtRef.current = 0;
      runReconciliationWithContext(false);
    };
    window.addEventListener('online', handleOnline);

    return () => {
      clearTimeout(initialTimer);
      clearTimeout(initialReconcile);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (reconciliationRef.current) {
        clearInterval(reconciliationRef.current);
        reconciliationRef.current = null;
      }
      if (pendingPollRef.current) {
        clearInterval(pendingPollRef.current);
        pendingPollRef.current = null;
      }
      window.removeEventListener('online', handleOnline);
    };
  }, [runSync, runReconciliationWithContext, pendingPollTick]);

  const fetchPendingOrders = useCallback(async () => {
    if (!window.electronAPI?.getOfflinePendingOrders) return [];
    try {
      return await window.electronAPI.getOfflinePendingOrders() || [];
    } catch {
      return [];
    }
  }, []);

  const deleteOfflineOrder = useCallback(async (localId) => {
    if (!window.electronAPI?.deleteOfflineOrder) return;
    try {
      await window.electronAPI.deleteOfflineOrder(localId);
      await refreshPendingCount();
    } catch (e) {
      console.error('[OfflineSync] deleteOfflineOrder failed:', e);
    }
  }, [refreshPendingCount]);

  const contextValue = window.electronAPI
    ? {
        pendingCount,
        isSyncing,
        syncNow: () => runReconciliationWithContext(true),
        refreshPendingCount,
        fetchPendingOrders,
        deleteOfflineOrder,
        kickOfflineReconcile,
      }
    : {
        pendingCount: 0,
        isSyncing: false,
        syncNow: () => {},
        refreshPendingCount: () => {},
        fetchPendingOrders: () => Promise.resolve([]),
        deleteOfflineOrder: () => Promise.resolve(),
        kickOfflineReconcile: () => {},
      };

  return (
    <OfflineContext.Provider value={contextValue}>
      {children}
    </OfflineContext.Provider>
  );
}
